import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';
import ReactLoader from 'react-loading';

import { withTheme, ThemeProps } from 'styled-components';

import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Airport } from 'models/airport';
import { VehiclePlace } from 'models/auto/vehicleSearch';
import { City } from 'models/city';
import { AirportsState } from 'reducers/airports';

import ResultItem from './components/ResultItem';
import ResultPlace from './components/ResultPlace';
import { Container, Header, Result, RecentSearches } from './styles';

interface Props extends AirportsState, ThemeProps<any> {
  show: boolean;
  placeHolder: string;
  minLengthForSearch: number;
  onClose: () => void;
  onSearchChange: (value: string) => void;
  onChange: (place?: Airport | City | VehiclePlace, isCity?: boolean) => void;
  isSearching: boolean;
  places: VehiclePlace[];
  isPickUp: boolean;
}

const PlacesSearch = ({
  show,
  placeHolder,
  bySearch,
  isLoading,
  isSearching,
  places,
  minLengthForSearch,
  theme,
  onClose,
  onChange,
  onSearchChange,
  isPickUp,
}: Props) => {
  const { t } = useStoreTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [isLocalSearching, setIsLocalSearching] = useState(false);

  useLayoutEffect(() => {
    searchInputRef.current?.focus();
    setSearchQuery('');
  }, [show]);

  useEffect(() => {
    setIsLocalLoading(isLoading);

    if (!isLoading) {
      const airports = isPickUp ? bySearch.pickup.airports : bySearch.dropoff.airports;
      const cities = isPickUp ? bySearch.pickup.cities : bySearch.dropoff.cities;

      if ((airports && airports.length > 0) || (cities && cities.length > 0)) {
        setIsLocalSearching(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    setIsLocalSearching(isSearching);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearching]);

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value && value.length > 2) {
      setIsLocalLoading(true);
      setIsLocalSearching(true);
    }

    setSearchQuery(value);
    onSearchChange(value);
  };

  const handleClickResultItem = (place: Airport | City | VehiclePlace, isCity?: boolean) => {
    onChange(place, isCity);
    onClose();
  };

  const renderLoading = () => (
    <Result.Loading>
      <ReactLoader type="spin" color={theme.loading} width={60} height={60} />
    </Result.Loading>
  );

  const renderAirports = () => {
    if (isPickUp && !bySearch.pickup) return null;
    if (!isPickUp && !bySearch.dropoff) return null;
    const airports = isPickUp ? bySearch.pickup.airports : bySearch.dropoff.airports;

    if (airports && airports.length > 0)
      return (
        <>
          <Result.Label>{t('autosSearch.airports')}</Result.Label>
          {airports.map((airport: Airport, index: number) => (
            <ResultItem
              key={`airport-${airport.code}-${index}`}
              data={airport}
              isCity={false}
              onClick={handleClickResultItem}
            />
          ))}
        </>
      );
    return null;
  };

  const renderPlaces = () => {
    if (places && places.length > 0)
      return (
        <>
          <Result.Label>{t('autosSearch.places')}</Result.Label>
          {places.map((place: VehiclePlace, index: number) => (
            <ResultPlace key={`place-${index}`} data={place} onClick={handleClickResultItem} />
          ))}
        </>
      );
    return null;
  };

  const renderResults = () => {
    if (isPickUp && !bySearch.pickup) return null;
    if (!isPickUp && !bySearch.dropoff) return null;

    const airports = isPickUp ? bySearch.pickup.airports : bySearch.dropoff.airports;
    const cities = isPickUp ? bySearch.pickup.cities : bySearch.dropoff.cities;
    if (
      !isLocalLoading &&
      !isLocalSearching &&
      cities.length === 0 &&
      airports.length === 0 &&
      searchQuery.length > minLengthForSearch &&
      places.length === 0
    ) {
      return (
        <Result.NotFound>
          <h2>Ops!</h2>
          <span>{t('error.notFound')}</span>
        </Result.NotFound>
      );
    }

    return (
      <Result data-testid="airport-search-result-list">
        {searchQuery.length <= minLengthForSearch && (
          <RecentSearches>{t('airportsSearch.recentSearches')}</RecentSearches>
        )}
        {(isLocalLoading || isLocalSearching) && renderLoading()}
        {renderAirports()}
        {renderPlaces()}
      </Result>
    );
  };

  return (
    <ApplicationOverlayContent sidebar show={show} onClose={onClose}>
      <Container>
        <Header>
          <Header.Search
            ref={searchInputRef}
            value={searchQuery}
            placeholder={placeHolder}
            onChange={handleSearchInputChange}
            data-testid="airport-search-input"
          />
          <Header.CloseIcon onClick={onClose} />
        </Header>
        {renderResults()}
      </Container>
    </ApplicationOverlayContent>
  );
};

export default withTheme(PlacesSearch);
